import * as React from 'react';
import { WBButton, WBFlex, WBCheckbox, WBBox } from '@admiin-com/ds-web';
import { styled, useMediaQuery, useTheme } from '@mui/material';
import { Bill } from '../PayBillCreateForm';
import { ManualBillForm } from './ManualBillForm';
import { t } from 'i18next';
import LoadingSpinner from '../../LoadingSpinner/LoadingSpinner';
import { ReviewForm } from './ReviewForm';
import { useBillCreate } from '../useBillCreate';
import BillModal from './BillModal';
import type { FieldErrors, FieldValues } from 'react-hook-form';
import { findFirstErrorField } from '../../../helpers/string';

interface Props {
  bills: Bill[];
  setBills: (bills: Bill[]) => void;
  manualBillInput: boolean;
  scanning: boolean;
  setScanning: (scanning: boolean) => void;
  onClose: () => void;
  draftTaskId: string | null;
}

export const ReviewBills = ({
  bills,
  setBills,
  manualBillInput,
  scanning,
  setScanning,
  onClose,
  draftTaskId,
}: Props) => {
  const [isDraftLoading, setIsDraftLoading] = React.useState(false);
  const [isAdding, setIsAdding] = React.useState(false);
  const [confirmationChecked, setConfirmationChecked] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const scrollToError = (errorFields: any) => {
    console.log(errorFields);
    // Helper function to recursively find the first error field

    const firstErrorField = findFirstErrorField(errorFields);
    if (firstErrorField) {
      const errorElement = document.getElementsByName(firstErrorField)?.[0];
      if (errorElement) {
        errorElement.scrollIntoView({ behavior: 'smooth' }); // Smoothly scroll to the field
      }
    }
  };

  const setErrors = async (
    index: number,
    errors: { path: string; message: string }[],
    fieldErrors?: FieldErrors<FieldValues>
  ) => {
    const errorObject: { [key: string]: string } = {};
    console.log('fieldErrors', fieldErrors);
    if (fieldErrors) {
      scrollToError(fieldErrors);
    }
    errors.forEach((error) => {
      errorObject[error.path] = error.message;
    });
    // console.log('errorObject', errorObject);
    // setBills(
    //   bills.map((bill, i) =>
    //     i === index ? { ...bill, errors: errorObject } : bill
    //   )
    // );
  };

  const { createBillsDraft, createBills } = useBillCreate({
    onErrorRow: async ({ index, errors, fieldErrors }) => {
      setErrors(index, errors, fieldErrors);
      if (manualBillInput) return;
      setOpened(index);
      if (isMobile) setEditBillModal(bills[index]);
    },
    draftTaskId: draftTaskId ?? undefined,
    onClose,
  });

  const handleDraft = async () => {
    try {
      setIsDraftLoading(true);
      await createBillsDraft(bills);
      onClose();
    } catch (error) {
      console.log(error);
    } finally {
      setIsDraftLoading(false);
    }
  };
  const isValid = bills.filter((bill) => bill.checked).length > 0;
  const billCount = bills.filter((bill) => bill.checked).length;

  const handleConfirm = async () => {
    try {
      setIsAdding(true);
      await createBills(bills);
      onClose();
    } catch (error) {
      console.error(error);
    } finally {
      setIsAdding(false);
    }
  };
  const [opened, setOpened] = React.useState<number>(-1);
  const [editBillModal, setEditBillModal] = React.useState<Bill | null>(null);
  return (
    <Container>
      {manualBillInput ? (
        <ManualBillForm
          bill={bills[0]}
          onUpdateBill={(bill) => setBills([bill])}
        />
      ) : (
        <ReviewForm
          bills={bills}
          isLoading={scanning}
          setBills={setBills}
          openedRow={opened}
          setOpenedRow={setOpened}
          onExit={onClose}
        />
      )}
      {editBillModal && (
        <BillModal
          item={editBillModal}
          open={!!editBillModal}
          onClose={() => {
            setEditBillModal(null);
          }}
          onOk={(newBill) => {
            setBills(
              bills.map((bill, i) =>
                i === opened ? { ...bill, ...newBill } : bill
              )
            );
            setEditBillModal(null);
          }}
        />
      )}
      <ButtonContainer>
        <WBBox textAlign="right">
          <WBCheckbox
            label={t('confirmDetailsCorrect', {
              ns: 'taskbox',
              defaultValue: 'I confirm the payment details are correct',
            })}
            checked={confirmationChecked}
            onChange={(e) => setConfirmationChecked(e.target.checked)}
            name="confirmDetails"
          />
        </WBBox>
        <ButtonWrapper>
          <WBButton
            variant="outlined"
            onClick={handleDraft}
            loading={isDraftLoading}
            sx={{ mt: 1, mr: 1 }}
            fullWidth
          >
            {t('saveAsDraft', { ns: 'taskbox' })}
          </WBButton>
          <WBButton
            loading={isAdding}
            type="submit"
            onClick={handleConfirm}
            sx={{ mt: 1, mr: 1 }}
            disabled={!isValid || !confirmationChecked}
            fullWidth
          >
            {billCount <= 1
              ? t('addBillToTaskboxSingle', { ns: 'taskbox', count: billCount })
              : t('addBillToTaskboxPlural', {
                  ns: 'taskbox',
                  count: billCount,
                })}
            &nbsp;
            {isAdding && <LoadingSpinner />}
          </WBButton>
        </ButtonWrapper>
      </ButtonContainer>
    </Container>
  );
};
const ButtonContainer = styled(WBFlex)(({ theme }) => ({
  flexDirection: 'column',
  width: '50%',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    width: '100%',
    flexDirection: 'column',
  },
}));

const Container = styled(WBFlex)(({ theme }) => ({
  flexDirection: 'column',
  flex: 1,
  gap: theme.spacing(1),
  width: '100%',
  alignItems: 'end',
  backgroundColor: theme.palette.background.default,
}));

const ButtonWrapper = styled(WBFlex)(({ theme }) => ({
  flexDirection: 'row',
  width: '100%',
  gap: theme.spacing(1),
}));
